import './migration-strategy.scss';

import { MigrationStrategySummary } from '@amzn/awscat-portfolio-assessment-types';
import {
    Checkbox,
    CheckboxProps,
    ExpandableSection,
    Input,
    InputProps,
    NonCancelableCustomEvent,
    Table,
    TableProps 
} from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { deepCopy } from '../../common/util';

interface Props {
    serverMigrationStrategySummary: MigrationStrategySummary[];
    serverInputSummary: MigrationStrategySummary[];
    databaseMigrationStrategySummary: MigrationStrategySummary[];
    databaseInputSummary: MigrationStrategySummary[];
    serverStrategyCountOverride: boolean;
    databaseStrategyCountOverride: boolean;
    updateServerStrategyCountOverride: (isOverride: boolean) => void;
    updateDatabaseStrategyCountOverride: (isOverride: boolean) => void;
    updateServerInputSummary: (updatedInputSummary: MigrationStrategySummary[]) => void;
    updateDatabaseInputSummary: (updatedInputSummary: MigrationStrategySummary[]) => void;
}

interface TableItem {
    strategy: string;
    serverCount: number;
    serverOverride: number;
    databaseCount: number;
    databaseOverride: number;
}

function MigrationStrategyCount(props: Props): JSX.Element {
    const [ items, setItems ] = useState<Array<TableItem>>([]);

    useEffect(() => {
        const getTotalsItem = (): TableItem => {
            const reduceFn = (total: number, strategyCount: MigrationStrategySummary) => {
                return total + strategyCount.count;
            };
    
            const totalCalculatedServers = props.serverMigrationStrategySummary.reduce(reduceFn, 0);
            const totalOverridenServers = items.reduce(overridenServerReduceFn, 0);
            const totalCalculatedDatabases = props.databaseMigrationStrategySummary.reduce(reduceFn, 0);
            const totalOverridenDatabases = items.reduce(overridenDatabaseReduceFn, 0);
    
            return {
                strategy: constants.TOTAL,
                serverCount: totalCalculatedServers,
                serverOverride: totalOverridenServers,
                databaseCount: totalCalculatedDatabases,
                databaseOverride: totalOverridenDatabases,
            };
        };
        const allStrategies: string[] = props.serverMigrationStrategySummary.map((strategyCount: MigrationStrategySummary) => strategyCount.strategy);

        const items: Array<TableItem> = allStrategies.map((strategy: string) => {
            const server = props.serverMigrationStrategySummary.find(x => x.strategy === strategy);
            const serverOverride = props.serverInputSummary.find(x => x.strategy === strategy);
            const database = props.databaseMigrationStrategySummary.find(db => db.strategy === strategy);
            const databaseOverride = props.databaseInputSummary.find(db => db.strategy === strategy);
            return { 
                strategy,
                serverCount: server?.count || 0,
                serverOverride: serverOverride?.count || 0,
                databaseCount: database?.count || 0,
                databaseOverride: databaseOverride?.count || 0,
            };
        });
        items.push(getTotalsItem());
        setItems(items);
    }, [props]);

    const overridenServerReduceFn = (total: number, tableItem: TableItem) => {
        if (tableItem.strategy !== constants.TOTAL) {
            return total + tableItem.serverOverride;
        } 
        return total;
    };

    const overridenDatabaseReduceFn = (total: number, tableItem: TableItem) => {
        if (tableItem.strategy !== constants.TOTAL) {
            return total + tableItem.databaseOverride;
        }
        return total;
    };

    const isDisabledRow = (item: TableItem): boolean => {
        return item.strategy === constants.TOTAL;
    };

    const getInputType = (item: TableItem): InputProps.Type => {
        return item.strategy === constants.TOTAL ? 'text' : 'number';
    };

    const onOverrideValueChange = (value: string, strategy: string, resource: 'server' | 'database'): void => { 
        let newValue = parseFloat(value);
        if (newValue < 0) { // value cannot be less than zero
            newValue = 0;
        }
        const updatedInputSummary: MigrationStrategySummary[] = deepCopy(resource === 'server' ? props.serverInputSummary : props.databaseInputSummary);
        const target = updatedInputSummary.find(summary => summary.strategy === strategy);
        if (!target) {
            updatedInputSummary.push({strategy: strategy, count: newValue});
        } else {
            target.count = newValue;
        }
        resource === 'server' ? props.updateServerInputSummary(updatedInputSummary) : props.updateDatabaseInputSummary(updatedInputSummary);
    };

    const overrideServerChanged = (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>): void => {
        props.updateServerStrategyCountOverride(event.detail.checked);
    };

    const calculatedServerChanged = (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>): void => {
        props.updateServerStrategyCountOverride(!event.detail.checked);
    };

    const overrideDatabaseChanged = (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>): void => {
        props.updateDatabaseStrategyCountOverride(event.detail.checked);
    };

    const calculatedDatabaseChanged = (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>): void => {
        props.updateDatabaseStrategyCountOverride(!event.detail.checked);
    };

    const calculatedServerHeader: React.ReactNode = (
        <Checkbox checked={!props.serverStrategyCountOverride} onChange={calculatedServerChanged}>{constants.CALC_SERVER}</Checkbox>
    );

    const overrideServerHeader: React.ReactNode = (
        <Checkbox checked={props.serverStrategyCountOverride} onChange={overrideServerChanged}>{constants.OVERRIDE_SERVER}</Checkbox>
    );

    const calculatedDatabaseHeader: React.ReactNode = (
        <Checkbox checked={!props.databaseStrategyCountOverride} onChange={calculatedDatabaseChanged} >{constants.CALC_DATABASE}</Checkbox>
    );

    const overrideDatabaseHeader: React.ReactNode = (
        <Checkbox checked={props.databaseStrategyCountOverride} onChange={overrideDatabaseChanged} >{constants.OVERRIDE_DATABASE}</Checkbox>
    );

    const columnDefinitions: TableProps.ColumnDefinition<TableItem>[] = [
        {
            id: 'strategies',
            header: '',
            cell: (item: TableItem) => item.strategy,
        },
        {
            id: constants.CALC_SERVER,
            header: calculatedServerHeader,
            cell: (item: TableItem) => <Input disabled value={`${item.serverCount}`} />
        },
        {
            id: constants.OVERRIDE_SERVER,
            header: overrideServerHeader,
            cell: (item: TableItem) => 
                <Input
                    value={item.serverOverride.toString()}
                    onChange={({ detail }) => onOverrideValueChange(detail.value, item.strategy, 'server')}
                    type={getInputType(item)}
                    disabled={isDisabledRow(item)}
                />
        },
        {
            id: constants.CALC_DATABASE,
            header: calculatedDatabaseHeader,
            cell: (item: TableItem) => <Input disabled value={`${item.databaseCount}`} />
        },
        {
            id: constants.OVERRIDE_DATABASE,
            header: overrideDatabaseHeader,
            cell: (item: TableItem) => 
                <Input
                    value={`${item.databaseOverride}`}
                    onChange={({ detail }) => onOverrideValueChange(detail.value, item.strategy, 'database')}
                    type={getInputType(item)}
                    disabled={isDisabledRow(item)}
                />
        }
    ];

    return (
        <ExpandableSection
            header={constants.COUNTS_HEADER}
        >
            <Table
                columnDefinitions={columnDefinitions}
                items={items}
                className="migration-strategy-count"
            />
        </ExpandableSection>
    );
}

export default MigrationStrategyCount;
